/// <amd-module name="DistributedTaskUI/LogReader/Parser" />

import { SubParser } from "DistributedTaskUI/LogReader/SubParser";
import { ILine, IFindResult, IParseNode } from "./Types";

export enum NodeType {
    Plain = 0,

    Command = 1,
    Debug = 2,
    Error = 3,
    Info = 4,
    Section = 5,
    Verbose = 6,
    Warning = 7
}

const plain = "plain";

const command = "command";
const debug = "debug";
const error = "error";
const info = "info";
const section = "section";
const verbose = "verbose";
const warning = "warning";

// Store the max command length we support, for example, we support "section", "command" which are of length 7, which highest of all others
const maxCommandLength = 7;
const supportedCommands = [command, debug, error, info, section, verbose, warning];

const commandToType: { [command: string]: NodeType } = {
    "command": NodeType.Command,
    "debug": NodeType.Debug,
    "error": NodeType.Error,
    "info": NodeType.Info,
    "section": NodeType.Section,
    "verbose": NodeType.Verbose,
    "warning": NodeType.Warning
};

const typeToCommand: { [type: string]: string } = {
    "0": plain,
    "1": command,
    "2": debug,
    "3": error,
    "4": info,
    "5": section,
    "6": verbose,
    "7": warning
};

const newLineChar = "\n";
const hashChar = "#";
const commandStart = "[";
const commandEnd = "]";

const spanStart = (className: string) => `<span class="dt-${className}">`;
const spanEnd = "</span>";

const unsetValue = -1;



export class Parser {
    private _subParser = new SubParser();

    /**
     * Parses the content into lines with nodes
     * @param content content to parse
     */
    public parse(content: string): ILine[] {
        // lines we return
        let lines: ILine[] = [];
        // accumulated nodes for a particular line
        let nodes: IParseNode[] = [];

        // start of a particular line
        let lineStart = 0;
        // start of plain node content
        let plainNodeStart = unsetValue;

        // tells to consider the default logic where we check for plain text etc.,
        let considerDefaultLogic = true;

        // stores the command, to match one of the 'supportedCommands'
        let command = "";
        // helps in finding commands in our format "##[command]" or "[command]"
        let commandSeeker = "";

        // when line ends, this tells if there's any pending node
        let pendingLastNode: number = unsetValue;

        const resetCommandVar = () => {
            commandSeeker = "";
            command = "";
        };

        const resetPlain = () => {
            plainNodeStart = unsetValue;
        };

        const resetPending = () => {
            pendingLastNode = unsetValue;
        };

        for (let index = 0; index < content.length; index++) {
            const char = content.charAt(index);
            // start with considering default logic, individual conditions are responsible to set it false
            considerDefaultLogic = true;
            if (char === newLineChar || index === content.length - 1) {
                // end of the line/text, push any final nodes
                if (pendingLastNode !== NodeType.Plain) {
                    // there's pending special node to be pushed
                    nodes.push({
                        type: pendingLastNode,
                        start: lineStart,
                        end: index
                    });
                }
                else if (pendingLastNode === NodeType.Plain) {
                    // there's pending plain node to be pushed
                    nodes.push({
                        type: NodeType.Plain,
                        start: plainNodeStart,
                        end: index
                    });
                }

                // end of the line, push all nodes that are accumulated till now
                lines.push({
                    nodes: nodes
                });
                // clear node as we are done with the line
                nodes = [];
                // increment lineStart for the next line
                lineStart = index + 1;
                // unset
                resetPlain();
                resetPending();
                resetCommandVar();

                considerDefaultLogic = false;
            }
            else if (char === hashChar) {
                // possible start of our well-known commands
                if (commandSeeker === "" || commandSeeker === "#") {
                    considerDefaultLogic = false;
                    commandSeeker += hashChar;
                }
            }
            else if (char === commandStart) {
                // possible continuation of our well-known commands
                if (commandSeeker === "##") {
                    considerDefaultLogic = false;
                    commandSeeker += commandStart;
                }
                else if (commandSeeker.length === 0) {
                    // covers - "", for live logs, commands will be of [section], with out "##"
                    considerDefaultLogic = false;
                    commandSeeker += commandStart;
                }

            }
            else if (char === commandEnd) {
                // possible continuation of our well-known commands
                const commandIndex = supportedCommands.indexOf(command);
                if (commandIndex !== -1) {
                    considerDefaultLogic = false;
                    // we reached the end and found the command
                    resetPlain();
                    // command is for the whole line, so we are not pushing the node here but defering this to when we find the new line
                    pendingLastNode = commandToType[command];
                }

                resetCommandVar();
            }

            if (considerDefaultLogic) {
                if (commandSeeker === "##[" || commandSeeker === "[") {
                    // it's possible that we are parsing a command
                    command += char.toLowerCase();
                }

                if (command.length > maxCommandLength) {
                    // to avoid accumulating command unncessarily, let's check max length, if it exceeds, it's not a command
                    resetCommandVar();
                }

                // considering as plain text
                if (plainNodeStart === unsetValue) {
                    // we didn't set this yet, set now
                    plainNodeStart = lineStart;
                    // set pending node if there isn't one pending
                    if (pendingLastNode === unsetValue) {
                        pendingLastNode = NodeType.Plain;
                    }

                }
            }
        }

        return lines;
    }

    /**
     * Converts parsed lines into HTML content
     * @param line Line to transform
     * @param raw Raw text to transform
     * @param lineNumber Current line number
     * @param findResult Optional find result so that we can parse it correctly
     */
    public transform(line: ILine, raw: string, lineNumber: number, findResult?: IFindResult): string {
        let result = "";
        line.nodes.forEach(x => {
            const name = typeToCommand[x.type];
            result += spanStart(name);
            // Note: Ansiparser sanatizes HTML as well
            result += this._subParser.parse(raw.substring(x.start, x.end), lineNumber, findResult);
            result += spanEnd;
        });
        return result;
    }
}
